import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext'; // Descomentar
import useAnalytics from '../../hooks/useAnalytics';
import SEO from '../../components/SEO/SEO';

const LoginPage = () => {
  return (
    <>
      <SEO
        title="Iniciar Sesión"
        description="Accede a tu cuenta de Resmoke para gestionar tus productos, favoritos y compras. Vende y compra en el marketplace de confianza."
        url="/login"
      />
      <LoginContent />
    </>
  );
};

const LoginContent = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackLogin, trackFormSubmit, trackError } = useAnalytics();

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const rememberUser = localStorage.getItem('rememberUser') === 'true';
    if (rememberUser && savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail, rememberMe: true }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Ingresa un correo electrónico válido';
    }
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setLoading(true);
    try {
      const { data, error } = await signInWithGoogle(credentialResponse);
      if (error) { setErrors({ general: error }); trackError('google_login_failed', error, '/login'); return; }
      trackLogin(data?.id || 'google_user', 'google');
      navigate(location.state?.from || '/');
    } catch (error) {
      setErrors({ general: 'Error al iniciar sesión con Google.' });
      trackError('google_login_error', error.message, '/login');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { trackFormSubmit('login_form', false); return; }
    setLoading(true);
    try {
      const { data, error } = await signIn(formData.email, formData.password);
      if (error) { setErrors({ general: error }); trackFormSubmit('login_form', false); trackError('email_login_failed', error, '/login'); return; }
      trackLogin(data?.id || data?.email, 'email');
      trackFormSubmit('login_form', true);
      if (formData.rememberMe) {
        localStorage.setItem('rememberUser', 'true');
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberUser');
        localStorage.removeItem('rememberedEmail');
      }
      navigate(location.state?.from || '/');
    } catch (error) {
      setErrors({ general: 'Error inesperado. Inténtalo de nuevo.' });
      trackFormSubmit('login_form', false);
      trackError('email_login_error', error.message, '/login');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="relative h-48 lg:h-auto lg:w-1/2 overflow-hidden">
        <img
          src="/img/resmoke/LoginRegister.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent lg:bg-gradient-to-r lg:from-black/80 lg:via-black/40 lg:to-transparent" />
        <div className="absolute bottom-6 left-6 lg:bottom-12 lg:left-12 z-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
            </div>
            <span className="text-white/80 text-sm font-medium tracking-wide">RESMOKE</span>
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">Vende y compra</h1>
          <p className="text-white/60 text-sm lg:text-base">con la comunidad más grande</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900">Iniciar Sesión</h2>
            <p className="text-gray-500 text-sm mt-1">Accede a tu cuenta de Resmoke</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.general && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-100 p-3 rounded-xl text-center">
                {errors.general}
              </div>
            )}

            <input
              type="email" name="email" value={formData.email} onChange={handleChange}
              placeholder="Correo electrónico"
              className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all ${
                errors.email ? 'border-red-300' : 'border-gray-200 hover:border-gray-300 focus:border-gray-900'
              }`}
              required autoFocus autoComplete="username"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"} name="password" value={formData.password}
                onChange={handleChange} placeholder="Contraseña"
                className={`w-full px-4 pr-11 py-3 rounded-xl border text-sm outline-none transition-all ${
                  errors.password ? 'border-red-300' : 'border-gray-200 hover:border-gray-300 focus:border-gray-900'
                }`}
                required autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                )}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={handleChange}
                  className="w-4 h-4 rounded border-gray-300" />
                Recordarme
              </label>
              <Link to="/forgot-password" className="text-gray-900 font-medium hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button type="submit" disabled={loading}
              className={`w-full py-3 rounded-xl text-sm font-medium transition-all ${
                loading
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 hover:bg-gray-800 text-white'
              }`}>
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-gray-400">o</span></div>
            </div>

            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() => setErrors({ general: 'Error al conectar con Google.' })}
              useOneTap={false} theme="outline" size="large" width="100%" text="signin_with" shape="rectangular"
            />

            <p className="text-center text-sm text-gray-500 pt-1">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="font-semibold text-gray-900 hover:underline">Regístrate</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
